import config from 'config';
import { add } from 'date-fns';
import i18n from 'i18n';
import { and, assign, fromPromise, not, or, setup } from 'xstate';
import {
  AppointmentPriceResponse,
  getAppointmentPrice,
  getStripeAccountIdByState,
  StripeAccountIdByStateResponse,
} from 'domains/billing';
import { getConsultationAvailableTimes, getScheduleAvailableTimesV4 } from 'domains/calendars/queries';
import { ScheduleSignupEvaluationResponse } from 'domains/evaluations/evaluationSchedule';
import { AvailableTimes, ScheduleAvailableTimes } from 'domains/calendars/types';
import { PrimaryLanguageEnum, RelationshipEnum } from 'domains/client';
import { TimeZone } from 'domains/timezone';
import { DEFAULT_APPOINTMENT_TYPE } from 'domains/signup/data/queryParams';
import { SignupMachineInput } from 'domains/signupv4/Parent/signupMachineV4';
import { MachineStateType } from 'utils/xstate/types';
import {
  scheduleConsultationLogic,
  ScheduleEvaluationErrorResponse,
  scheduleEvaluationLogic,
} from 'domains/signupv4/Parent/actors';
import { CompletedMiniScreenerMachineData } from 'domains/signupv4/Mini-Screener/miniScreenerMachine';
import { TherapyTarget, EnterMedicaidInsuranceForm } from 'domains/signup/data/types';

const AVAILABLE_TIMES_OFFSET_SELF_PAY = 3;
const AVAILABLE_TIMES_OFFSET_MEDICAID = 7;

const AVAILABLE_TIMES_LIMIT_SELF_PAY = 2 * 7 + AVAILABLE_TIMES_OFFSET_SELF_PAY;
const AVAILABLE_TIMES_LIMIT_MEDICAID = 2 * 7 + AVAILABLE_TIMES_OFFSET_MEDICAID;

const generateDefaultAvailableTimes = (useInsurance: boolean) => {
  const generatedDates: ScheduleAvailableTimes[] = [];
  const availableTimesOffset = useInsurance ? AVAILABLE_TIMES_OFFSET_MEDICAID : AVAILABLE_TIMES_OFFSET_SELF_PAY;
  const availableTimesLimit = useInsurance ? AVAILABLE_TIMES_LIMIT_MEDICAID : AVAILABLE_TIMES_LIMIT_SELF_PAY;

  for (let i = availableTimesOffset; i <= availableTimesLimit; i++) {
    const now = new Date(`${new Date().toISOString().split('T')[0]}T12:00:00.000Z`);
    generatedDates.push({ day: add(now, { days: i }), availableTimes: [] });
  }

  return generatedDates;
};

export type CompletedScheduleMachineContext = {
  amount: number;
  availableConsultDates: AvailableTimes;
  availableEvalTimes: ScheduleAvailableTimes[];
  calendarID: number;
  consultationDate: Date;
  consultationTimezone: TimeZone;
  evaluationDate: Date;
  evaluationTimezone: TimeZone;
  selectedDate: Date;
  selectedDateIndex: number;
  stripeAccountId: string;
  stripePaymentMethodId: string;
  skipPayment: boolean;
  therapistName: string;
  errorStripeCode?: string;
  errorStripeDeclineCode?: string;
  errorMessage?: string;
  errorStatus?: string;
  statusCode?: number;
  scheduleResponse?: ScheduleSignupEvaluationResponse;
};

export type ScheduleResult = {
  data: CompletedScheduleMachineContext;
  earlyExit?: boolean;
};

export type ScheduleMachineInput = {
  machineConfig: Pick<SignupMachineInput, 'calendarID'> &
    Required<Pick<SignupMachineInput, 'appointmentType'>> & {
      state: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      leadID: string;
      schedulingID: string;
      target: TherapyTarget;

      miniScreenerInfo: CompletedMiniScreenerMachineData;

      useInsurance: boolean;
      insuranceEligibility: string;
      insuranceType: string;
      insuranceForm: EnterMedicaidInsuranceForm;

      appointmentType: string;
      calendarID?: string;

      canExitByBack?: boolean;
    };
};

type InternalContext = {
  controller: AbortController;
  data: Partial<CompletedScheduleMachineContext>;
  machineConfig: ScheduleMachineInput['machineConfig'];
  isConsultation?: boolean;
  earlyExit?: boolean;
};

/** @xstate-layout N4IgpgJg5mDOIC5SwMYAtIFcA2YCyAhugJYB2YAdGKQC5gBOAwgPamw40E3GsAiXYAMTsARgFtiNFmw5cepfnQDaABgC6iUAAdmsSfM0gAHogC0AFgDsligCYVAVgAcANhUBGR+YCcv8wBoQAE9EAGZbbwpvd1tbF1Cnb3sI2wBfVMDUDAgcfCI0MkoUVgAzYnoxaXZsTm4+AUERIgBrVQ0kEB09OtJDEwRTS3cXCndLJ3NJ0KSnWwTAkIRQh1sKeKdZ83dvaxVLbwd0zPQsXEIScgpi0jKKqtkexSFr27FIe5q5euV1Qy79Vh9MzmRIUaahULmUIqKGxHwLRDxVZOSzmBz7FxOFS2dzuI4gLKnPIXSjYZgECBkKAAQQAbgRiNgCCJcE9YIIIKxKGRacxmpRCTkzvlChQyRSqXSGUyWWA2QgecwUF9SG02n9dADeh1+hZvCoKIkIeYXJZnOjxgiBjjRpCDt4JjCrC40hkCSchcSCpdxZTSDT6YzmayBOyGPRmPQKFomTQSpGxBRBblzt7SeS-QHpcG5aGFaRecqemrfh1-j0gQN3KERtssd5JujQnWnFbTDEbLtnNtbJsJk50m7SMwIHBDMnhSSNd0DDqzJYXOZRuNJlCZnNW8EzLZzAbbJZHE4HA5HLEHGj8ROvaLqHQmKxqrV5E9p1rKxYXJEHJ4HOEHJjsTmSwrWGUI1jmb8QQ2U0IRcS8PRTEVLhecpKgfB5nwEV8KznAYrCcOxqxceJzxcYZzFsK1v1WRdXDRM1pgdQ43SvVNRV9SVAxlEM6HgMtNRw0BdRPJdd1xVx1ksexvBcNt3BWCgu0SFRMVRBd4OyRCSTFDMqTZAAxaVIGw2chLMTxVlhNEXGcDw9jNK0DRUaI3BNSxlmGFQUQcbwNKJNjLjKUhiFgbITMBXCLFCGxwmhfcQXCJxjStcYHFGF1YgSHEpNcQdUiAA */
export const scheduleMachineV4 = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'cancelLoadingAvailableEvalDates' }
      | { type: 'checkUseInsurance'; useInsurance: boolean }
      | { type: 'initAvailableEvalTimes' }
      | { type: 'submitSelectedDate'; selectedDate: Date; selectedDateIndex: number }
      | {
          type: 'submitEvaluationInfo';
          calendarID: number;
          evaluationDate: Date;
          evaluationTimezone: TimeZone;
          therapistName: string;
        }
      | { type: 'confirmedEvaluationDate'; stripePaymentMethodId: string | undefined }
      | { type: 'submitConsultationDate'; consultationDate: Date; consultationTimezone: TimeZone }
      | { type: 'confirmedConsultationDate' }
      | { type: 'confirmConsultation' }
      | { type: 'confirmTherapySession' }
      | { type: 'clearError' },
    input: {} as ScheduleMachineInput,
    output: {} as ScheduleResult,
  },

  actions: {
    preloadEvalDates: ({ context }) => {
      const date = add(new Date(), { days: 7 });

      getScheduleAvailableTimesV4({
        date,
        insuranceName: context.machineConfig.insuranceForm?.insuranceName,
        signal: new AbortController().signal,
        state: context.machineConfig.state,
        specialties: context.machineConfig.miniScreenerInfo.detectedAreas.join(';'),
        prewarm: true,
      }).catch(() => {});
    },
    saveConsultationDate: assign(({ context }, params: { consultationDate: Date; consultationTimezone: TimeZone }) => ({
      data: {
        ...context.data,
        consultationDate: params.consultationDate,
        consultationTimezone: params.consultationTimezone,
      },
    })),
    saveEvaluationInfo: assign(
      (
        { context },
        params: { calendarID: number; evaluationDate: Date; evaluationTimezone: TimeZone; therapistName: string },
      ) => ({
        data: {
          ...context.data,
          calendarID: params.calendarID,
          evaluationDate: params.evaluationDate,
          evaluationTimezone: params.evaluationTimezone,
          therapistName: params.therapistName,
        },
      }),
    ),
    saveSelectedDate: assign(({ context }, params: { selectedDate: Date; selectedDateIndex: number }) => ({
      data: {
        ...context.data,
        selectedDate: params.selectedDate,
        selectedDateIndex: params.selectedDateIndex,
        errorMessage: undefined,
      },
    })),
    saveAvailableConsultDates: assign(({ context }, params: { data: AvailableTimes }) => ({
      data: {
        ...context.data,
        availableConsultDates: params.data,
      },
    })),
    saveAvailableEvalTimes: assign(({ context }) => {
      const availableEvalTimes = generateDefaultAvailableTimes(context.machineConfig.useInsurance);

      return {
        data: {
          ...context.data,
          availableEvalTimes,
          errorMessage: undefined,
        },
      };
    }),
    saveErrorMessage: assign(
      (
        { context },
        event: {
          errorStripeCode: string;
          errorStripeDeclineCode: string;
          errorMessage: string;
          errorStatus: string;
          statusCode: number;
        },
      ) => ({
        data: {
          ...context.data,
          errorStripeCode: event.errorStripeCode,
          errorStripeDeclineCode: event.errorStripeDeclineCode,
          errorMessage: event.errorMessage,
          errorStatus: event.errorStatus,
          statusCode: event.statusCode,
        },
      }),
    ),
    saveAvailableEvalDates: assign(({ context }, params: { data: ScheduleAvailableTimes }) => {
      const availableEvalTimes = context.data.availableEvalTimes ?? [];

      if (context.data.selectedDateIndex !== undefined && Number.isFinite(context.data.selectedDateIndex)) {
        availableEvalTimes[context.data.selectedDateIndex] = params.data;
      }

      return {
        data: {
          ...context.data,
          availableEvalTimes,
          errorMessage: undefined,
        },
      };
    }),
    saveEvaluationPrice: assign(({ context }, params: { data: number }) => ({
      data: {
        ...context.data,
        amount: params.data,
      },
    })),
    saveStripeAccountId: assign(({ context }, params: { data: string }) => ({
      data: {
        ...context.data,
        stripeAccountId: params.data,
      },
    })),
    saveStripePaymentMethodId: assign(({ context }, params: { data: string | undefined }) => ({
      data: {
        ...context.data,
        stripePaymentMethodId: params.data,
      },
    })),
    saveScheduleResponse: assign(({ context }, params: { scheduleResponse?: ScheduleSignupEvaluationResponse }) => ({
      data: {
        ...context.data,
        scheduleResponse: params.scheduleResponse,
      },
    })),
    redirectOnComplete: ({ context }) => {
      const redirectURL = context.data.scheduleResponse?.invitationLink;
      if (redirectURL) {
        window.location.replace(redirectURL);
      }
    },
  },

  actors: {
    getAvailableConsultDates: fromPromise<
      AvailableTimes,
      Pick<InternalContext['machineConfig'], 'appointmentType' | 'calendarID'>
    >(async ({ input }) => {
      return getConsultationAvailableTimes({
        appointmentType: input.appointmentType,
        calendarID: input.calendarID,
      });
    }),
    getAvailableEvalDates: fromPromise<
      ScheduleAvailableTimes,
      { date?: Date; insuranceName: string | undefined; signal: AbortSignal; state: string; specialties: string[] }
    >(async ({ input }) => {
      if (!input.date) {
        throw new Error('Missing required input.');
      }

      return getScheduleAvailableTimesV4({
        date: input.date,
        insuranceName: input.insuranceName,
        signal: input.signal,
        state: input.state,
        specialties: input.specialties.join(';'),
      });
    }),
    getAppointmentPriceAmount: fromPromise<AppointmentPriceResponse, { type: 'session' | 'evaluation'; tier?: string }>(
      async ({ input }) => {
        return getAppointmentPrice(input.type, input.tier);
      },
    ),
    getStripeAccountId: fromPromise<StripeAccountIdByStateResponse, { state: string }>(async ({ input }) => {
      return getStripeAccountIdByState(input.state);
    }),
    scheduleConsultation: scheduleConsultationLogic,
    scheduleEvaluation: scheduleEvaluationLogic,
  },
  guards: {
    canExitEarly: ({ context }) => context.machineConfig.canExitByBack === true,
    isConsultation: ({ context }) => {
      if (!context.machineConfig.miniScreenerInfo) {
        return true; // could happen after a browser refresh
      }

      return (
        (context.machineConfig.useInsurance === true && context.machineConfig.insuranceType !== 'medicaid') || // if client is using insurance and not medicaid
        context.machineConfig.miniScreenerInfo.previousEvaluation === true || // if client has had a previous evaluation last 6 months
        context.machineConfig.miniScreenerInfo.primaryLanguage !== PrimaryLanguageEnum.English || // if client's primary language is not english
        !(
          (
            context.machineConfig.miniScreenerInfo.concerns.fullConcerns?.length &&
            context.machineConfig.miniScreenerInfo.concerns.fullConcerns.every(concern => concern.allowEval === true)
          ) // if client has concerns that are not allowed for evaluation
        ) ||
        (context.machineConfig.target === 'child' &&
          ![RelationshipEnum.Parent, RelationshipEnum.LegalGuardian].includes(
            // if client's relationship to client is not parent or legal guardian
            context.machineConfig.miniScreenerInfo.relationshipToClient,
          ))
      );
    },
    isConcernAllowedForEvaluation: ({ context }) => {
      return !!(
        context.machineConfig.miniScreenerInfo?.concerns.fullConcerns?.length &&
        context.machineConfig.miniScreenerInfo.concerns.fullConcerns.every(concern => concern.allowEval === true)
      );
    },
    isInsurance: ({ context }) => context.machineConfig.useInsurance === true,
    isInsuranceEligibilityInn: ({ context }) => {
      return context.machineConfig.insuranceEligibility === 'inn';
    },
    isInsuranceEligible: ({ context }) => {
      return ['inn', 'inactive'].includes(context.machineConfig.insuranceEligibility);
    },
    isSelfPay: ({ context }) => context.machineConfig.useInsurance === false,
    isInsuranceAndEligibilityInactive: ({ context }) => {
      return context.machineConfig.useInsurance === true && context.machineConfig.insuranceEligibility === 'inactive';
    }
  },
}).createMachine({
  id: 'scheduleMachine',
  initial: 'start',

  context: ({ input }) => ({
    controller: new AbortController(),
    machineConfig: {
      ...input.machineConfig,
      appointmentType: input.machineConfig.appointmentType ?? DEFAULT_APPOINTMENT_TYPE,
    },
    data: {},
    earlyExit: false,
  }),

  on: {
    clearError: {
      actions: assign(({ context }) => ({
        data: {
          ...context.data,
          errorStripeCode: undefined,
          errorStripeDeclineCode: undefined,
          errorMessage: undefined,
          errorStatus: undefined,
          statusCode: undefined,
        },
      })),
    },
  },

  states: {
    start: {
      always: [
        {
          target: 'continueToConsultation',
          guard: and(['isConsultation', 'isInsuranceEligibilityInn']),
        },
        {
          target: 'enterAppointmentType',
          guard: and([not('isConsultation'), or(['isInsuranceEligible', 'isSelfPay'])]),
        },
        {
          target: 'excludedFromSelfSignup',
          guard: not('isConcernAllowedForEvaluation'),
        },
        {
          // fallback should be always send the user to consultation
          target: 'redirectToConsultation',
        },
      ],
    },

    continueToConsultation: {
      on: {
        confirmConsultation: {
          target: 'loadingAvailableConsultDates',
        },

        back: { target: 'finished', actions: assign({ earlyExit: true }), guard: 'canExitEarly' },
      },
    },

    enterAppointmentType: {
      entry: ['preloadEvalDates'],
      on: {
        confirmConsultation: {
          target: 'loadingAvailableConsultDates',
        },

        confirmTherapySession: {
          target: 'enterEvaluationDate',
        },

        back: { target: 'finished', actions: assign({ earlyExit: true }), guard: 'canExitEarly' },
      },
    },

    excludedFromSelfSignup: {
      after: {
        500: {
          target: 'loadingAvailableConsultDates',
        },
      },
    },

    // this is for mixpanel to register the state change and report when the user is ineligible for evaluation
    // it just runs the loadingAvailableConsultDates state under the hood
    redirectToConsultation: {
      after: {
        500: {
          target: 'loadingAvailableConsultDates',
        },
      },
    },

    enterConsultationDate: {
      on: {
        submitConsultationDate: {
          target: 'confirmConsultationDate',

          actions: {
            type: 'saveConsultationDate',
            params: ({ event: { consultationDate, consultationTimezone } }) => ({
              consultationDate,
              consultationTimezone,
            }),
          },
        },

        back: [
          {
            target: 'continueToConsultation',
            guard: and(['isConsultation', 'isInsuranceEligibilityInn']),
          },
          {
            target: 'enterAppointmentType',
            guard: and([not('isConsultation'), or(['isInsuranceEligible', 'isSelfPay'])]),
          },
          {
            target: 'finished',
            actions: assign({ earlyExit: true }),
            guard: 'canExitEarly',
          },
        ],
      },
    },

    enterEvaluationDate: {
      on: {
        initAvailableEvalTimes: [
          {
            target: 'loadingAvailableEvalDates',
            guard: ({ context }) => context.data.selectedDate !== undefined,
          },
          {
            actions: {
              type: 'saveAvailableEvalTimes',
            },
          },
        ],

        submitSelectedDate: {
          target: 'loadingAvailableEvalDates',

          actions: [
            assign({ controller: () => new AbortController() }),
            {
              type: 'saveSelectedDate',
              params: ({ event: { selectedDate, selectedDateIndex } }) => ({
                selectedDate,
                selectedDateIndex,
              }),
            },
          ],
        },

        submitEvaluationInfo: [
          {
            target: 'loadingEvaluationPrice',
            guard: or(['isInsuranceAndEligibilityInactive', 'isSelfPay']),

            actions: {
              type: 'saveEvaluationInfo',
              params: ({ event: { calendarID, evaluationDate, evaluationTimezone, therapistName } }) => ({
                calendarID,
                evaluationDate,
                evaluationTimezone,
                therapistName,
              }),
            },
          },
          {
            target: 'confirmEvaluationDate',

            actions: {
              type: 'saveEvaluationInfo',
              params: ({ event: { calendarID, evaluationDate, evaluationTimezone, therapistName } }) => ({
                calendarID,
                evaluationDate,
                evaluationTimezone,
                therapistName,
              }),
            },
          },
        ],

        back: 'enterAppointmentType',
      },
    },

    confirmConsultationDate: {
      on: {
        back: 'enterConsultationDate',
        confirmedConsultationDate: 'schedulingConsultation',
      },
    },

    confirmEvaluationDate: {
      on: {
        back: 'enterEvaluationDate',
        confirmedEvaluationDate: {
          target: 'schedulingEvaluation',

          actions: {
            type: 'saveStripePaymentMethodId',
            params: ({ event: { stripePaymentMethodId } }) => ({
              data: stripePaymentMethodId,
            }),
          },
        },
      },
    },

    loadingAvailableConsultDates: {
      invoke: {
        src: 'getAvailableConsultDates',
        input: ({
          context: {
            machineConfig: { appointmentType, calendarID },
          },
        }) => ({ appointmentType: appointmentType, calendarID }),
        onDone: {
          target: 'enterConsultationDate',
          actions: {
            type: 'saveAvailableConsultDates',
            params: ({ event: { output } }) => ({
              data: output,
            }),
          },
        },
        onError: 'loadingAvailableConsultDatesFailed',
      },
    },

    loadingAvailableConsultDatesFailed: {},

    loadingAvailableEvalDates: {
      on: {
        cancelLoadingAvailableEvalDates: {
          target: 'enterEvaluationDate',

          actions: ({ context }) => {
            if (context.controller) {
              context.controller.abort();
            }
          },
        },
      },
      invoke: {
        src: 'getAvailableEvalDates',
        input: ({
          context: {
            controller,
            data: { selectedDate: date },
            machineConfig: {
              state,
              miniScreenerInfo: { detectedAreas },
              insuranceForm: { insuranceName } = {},
            },
          },
        }) => ({ date, insuranceName, signal: controller.signal, state, specialties: detectedAreas }),
        onDone: {
          target: 'enterEvaluationDate',
          actions: {
            type: 'saveAvailableEvalDates',
            params: ({ event: { output } }) => ({
              data: output,
            }),
          },
        },
        onError: [
          {
            target: 'enterEvaluationDate',
            guard: ({ context }) => context.controller.signal.aborted,
          },
          {
            target: 'loadingAvailableEvalDatesFailed',
          },
        ],
      },
    },

    loadingAvailableEvalDatesFailed: {
      after: {
        500: 'enterEvaluationDate',
      },
    },

    loadingEvaluationPrice: {
      invoke: {
        src: 'getAppointmentPriceAmount',
        input: () => ({ type: 'evaluation' }),
        onDone: {
          target: 'loadingStripeAccountId',
          actions: {
            type: 'saveEvaluationPrice',
            params: ({ event: { output } }) => ({
              data: output.value,
            }),
          },
        },
        onError: 'loadingEvaluationPriceFailed',
      },
    },

    // if the evaluation price fetch fails, we should redirect the user to consultation
    loadingEvaluationPriceFailed: {
      after: {
        500: 'loadingAvailableConsultDates',
      },
    },

    loadingStripeAccountId: {
      invoke: {
        src: 'getStripeAccountId',
        input: ({ context }) => ({ state: context.machineConfig.state }),
        onDone: {
          target: 'confirmEvaluationDate',
          actions: [
            {
              type: 'saveStripeAccountId',
              params: ({ event: { output } }) => ({
                data: output.accountId,
              }),
            },
          ],
        },
        onError: 'loadingAvailableConsultDates',
      },
    },

    // if the stripe account id fetch fails, we should redirect the user to consultation
    loadingStripeAccountIdFailed: {
      after: {
        500: 'loadingAvailableConsultDates',
      },
    },

    schedulingConsultation: {
      invoke: {
        src: 'scheduleConsultation',
        input: ({ context }) =>
          ({
            ...context.data,
            ...context.machineConfig,
            calendarID: context.data.calendarID ?? context.machineConfig.calendarID,
          } as Required<CompletedScheduleMachineContext & ScheduleMachineInput['machineConfig']>),
        onDone: {
          target: 'finished',
        },
        onError: {
          target: 'schedulingConsultationFailed',
        },
      },
    },

    schedulingConsultationFailed: {},

    schedulingEvaluation: {
      invoke: {
        src: 'scheduleEvaluation',
        input: ({ context }) =>
          ({
            ...context.data,
            ...context.machineConfig,
            calendarID: context.data.calendarID ?? context.machineConfig.calendarID,
          } as Required<CompletedScheduleMachineContext & ScheduleMachineInput['machineConfig']>),
        onDone: {
          target: 'finished',
          actions: {
            type: 'saveScheduleResponse',
            params: ({ event }) => {
              if (event.output) {
                return { scheduleResponse: event.output };
              } else {
                return {};
              }
            },
          },
        },
        onError: {
          target: 'schedulingEvaluationFailed',
          actions: {
            type: 'saveErrorMessage',
            params: ({ event }) => {
              const { originalError: error } = event.error as ScheduleEvaluationErrorResponse;

              return {
                errorStripeCode: error.response?.data.error?.code ?? 'unknown',
                errorStripeDeclineCode: error.response?.data.error?.decline_code ?? 'unknown',
                errorMessage:
                  error.response?.data.message ||
                  i18n.t('appointmentPayment.error.unexpected', { supportEmail: config.get('supportEmail') }),
                errorStatus: error.response?.data.status ?? 'unknown',
                statusCode: error.response?.status ?? -1,
              };
            },
          },
        },
      },
    },

    schedulingEvaluationFailed: {
      after: {
        500: [
          {
            target: 'contactSupport',
            guard: ({ context }) =>
              ['client_creation_failed', 'eval_creation_failed'].includes(context.data.errorStatus ?? ''),
          },
          {
            target: 'confirmEvaluationDate',
          },
        ],
      },
    },

    contactSupport: {},

    finished: {
      type: 'final',
    },
  },
  output: ({ context }) => context as ScheduleResult,
});

export type ScheduleState = MachineStateType<typeof scheduleMachineV4>;
